import React from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import { Link } from "react-router-dom";
import { useConfig } from "../../../context/ConfigContext";

const ValuesSection = () => {
  const config = useConfig();
  const { COMPANY_NAME, GARANTY_YEARS, INICIO_DESDE } = config;
  const coreValues = [
    {
      id: 1,
      title: "Transparencia Total",
      description:
        "Comunicación clara y honesta en cada etapa del proceso, desde la cotización hasta el mantenimiento post-instalación.",
      icon: "Eye",
      color: "primary",
      principles: [
        "Cotizaciones detalladas sin costos ocultos",
        "Visualización de progreso en tiempo real",
        "Acceso completo a datos de rendimiento",
        "Comunicación con el equipo técnico",
      ],
      commitment: "Garantizamos información veraz y accesible en todo momento",
    },

    {
      id: 3,
      title: "Excelencia Técnica",
      description:
        "Estándares internacionales de calidad aplicados con conocimiento profundo de las condiciones locales guatemaltecas.",
      icon: "Award",
      color: "accent",
      principles: [
        "Calidad en nuestros productos",
        "Capacitación técnica de nuestro personal",
        "Uso de tecnología de vanguardia",
        "Controles de calidad",
      ],
      commitment:
        "Superamos los estándares de la industria en cada instalación",
    },
    {
      id: 4,
      title: "Compromiso a Largo Plazo",
      description:
        "Relaciones duraderas con nuestros clientes, basadas en confianza mutua y soporte continuo durante décadas.",
      icon: "Handshake",
      color: "success",
      principles: [
        "Garantías respaldadas por las marcas",
        "Mantenimiento preventivo",
        "Soporte técnico",
      ],
      commitment: `Tu socio energético para los próximos ${GARANTY_YEARS} años`,
    },
    {
      id: 5,
      title: "Innovación Constante",
      description:
        "Adopción temprana de nuevas tecnologías y metodologías para ofrecer las mejores soluciones energéticas.",
      icon: "Lightbulb",
      color: "warning",
      principles: [
        "Alianzas con fabricantes líderes",
        "Monitoreo inteligente",
        "Mejora continua de procesos",
      ],
      commitment: "Siempre a la vanguardia de la tecnología solar",
    },
  ];

  const customerPromises = [
    {
      title: "Precio Justo Garantizado",
      description:
        "Precios competitivos sin sacrificar calidad, con financiamiento accesible",
      icon: "DollarSign",
    },
    {
      title: "Instalación Profesional",
      description: "Técnicos con experiencia en condiciones guatemaltecas",
      icon: "Wrench",
    },
    {
      title: "Soporte Vitalicio",
      description:
        "Acompañamiento continuo durante toda la vida útil del sistema",
      icon: "Shield",
    },
    {
      title: "Resultados Medibles",
      description:
        "Monitoreo en tiempo real y reportes detallados de rendimiento",
      icon: "BarChart3",
    },
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      primary: "bg-primary/10 text-primary",
      secondary: "bg-secondary/10 text-secondary",
      accent: "bg-accent/10 text-accent",
      success: "bg-success/10 text-success",
      warning: "bg-warning/10 text-warning",
      error: "bg-error/10 text-error",
    };
    return colorMap?.[color] || "bg-primary/10 text-primary";
  };

  const getIconColor = (color) => {
    const colorMap = {
      primary: "var(--color-primary)",
      secondary: "var(--color-secondary)",
      accent: "var(--color-accent)",
      success: "var(--color-success)",
      warning: "var(--color-warning)",
      error: "var(--color-error)",
    };
    return colorMap?.[color] || "var(--color-primary)";
  };

  return (
    <section className="py-20 bg-gradient-to-b from-muted to-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
            <Icon name="Heart" size={20} color="var(--color-primary)" />
            <span className="text-primary font-semibold">Nuestros Valores</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
            Principios que Guían
            <span className="solar-text-gradient block">Cada Decisión</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Nuestros valores no son solo palabras en la pared. Son compromisos
            tangibles que se reflejan en cada interacción, cada instalación y
            cada relación que construimos.
          </p>
        </div>

        {/* Core Values Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-20">
          {coreValues?.map((value) => (
            <div
              key={value?.id}
              className="group bg-white rounded-3xl p-8 border border-gray-100 solar-shadow-card solar-hover-lift relative overflow-hidden transition-all duration-300 hover:border-primary/20 hover:shadow-2xl"
            >
              {/* Side accent bar */}
              <div
                className={`absolute left-0 top-0 bottom-0 w-1.5 opacity-20 group-hover:opacity-100 transition-opacity`}
                style={{ backgroundColor: getIconColor(value?.color) }}
              ></div>

              <div className="flex items-start gap-6 mb-8">
                <div
                  className={`w-16 h-16 rounded-2xl flex-shrink-0 flex items-center justify-center transition-all duration-300 group-hover:scale-110 shadow-sm ${getColorClasses(
                    value?.color
                  )}`}
                >
                  <Icon
                    name={value?.icon}
                    size={32}
                    color={getIconColor(value?.color)}
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-text-primary mb-3 group-hover:text-primary transition-colors">
                    {value?.title}
                  </h3>
                  <p className="text-text-secondary leading-relaxed text-sm lg:text-base">
                    {value?.description}
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100/50">
                  <h4 className="text-xs font-bold text-text-primary uppercase tracking-widest mb-4 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: getIconColor(value?.color) }}></div>
                    Principios de Ejecución
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {value?.principles?.map((principle, index) => (
                      <div key={index} className="flex items-start gap-2.5">
                        <Icon
                          name="CheckCircle"
                          size={14}
                          color={getIconColor(value?.color)}
                          className="mt-0.5 flex-shrink-0 opacity-70"
                        />
                        <span className="text-text-secondary text-xs lg:text-sm font-medium">
                          {principle}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  className="p-5 rounded-2xl border-l-4 transition-all duration-300 group-hover:translate-x-1"
                  style={{
                    borderLeftColor: getIconColor(value?.color),
                    backgroundColor: `${getIconColor(value?.color)}08`
                  }}
                >
                  <div className="flex gap-3">
                    <Icon
                      name="Quote"
                      size={18}
                      color={getIconColor(value?.color)}
                      className="flex-shrink-0 mt-0.5 opacity-40"
                    />
                    <p className="text-text-primary font-bold text-sm italic leading-snug">
                      {value?.commitment}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Customer Promise Section */}
        <div className="bg-white rounded-3xl p-8 lg:p-12 solar-shadow-card mb-16">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-text-primary mb-4">
              Nuestra Promesa al Cliente
            </h3>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Estos compromisos específicos definen la experiencia que cada
              cliente puede esperar al elegir {COMPANY_NAME} como su socio
              energético.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {customerPromises?.map((promise, index) => (
              <div key={index} className="group text-center p-6 rounded-2xl hover:bg-gray-50 transition-colors duration-300">
                <div className="w-20 h-20 mx-auto mb-6 bg-primary/5 rounded-full flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all duration-500">
                  <Icon
                    name={promise?.icon}
                    size={36}
                    className="text-primary group-hover:text-white transition-colors duration-300"
                  />
                </div>
                <h4 className="text-lg font-bold text-text-primary mb-3">
                  {promise?.title}
                </h4>
                <p className="text-text-secondary text-sm leading-relaxed">
                  {promise?.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Values in Action */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-text-primary">
              Valores en Acción
            </h3>
            <p className="text-text-secondary leading-relaxed">
              Nuestros valores se manifiestan en decisiones concretas que
              tomamos todos los días. Desde la selección de materiales hasta el
              trato con cada cliente, cada acción refleja nuestro compromiso con
              Guatemala y su futuro energético.
            </p>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon name="Check" size={14} color="white" />
                </div>
                <div>
                  <h4 className="font-semibold text-text-primary">
                    Decisiones Éticas
                  </h4>
                  <p className="text-text-secondary text-sm">
                    Priorizamos el beneficio del cliente sobre las ganancias a
                    corto plazo
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon name="Check" size={14} color="white" />
                </div>
                <div>
                  <h4 className="font-semibold text-text-primary">
                    Impacto Medible
                  </h4>
                  <p className="text-text-secondary text-sm">
                    Cada proyecto contribuye cuantificablemente a los objetivos
                    ambientales
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon name="Check" size={14} color="white" />
                </div>
                <div>
                  <h4 className="font-semibold text-text-primary">
                    Compromiso Duradero
                  </h4>
                  <p className="text-text-secondary text-sm">
                    Mantenemos relaciones de{" "}
                    {new Date()?.getFullYear() - INICIO_DESDE}+ años con
                    nuestros clientes
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="rounded-2xl overflow-hidden solar-shadow-elevated">
              <Image
                src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=600&h=500&fit=crop"
                alt="Equipo de Solar Valley trabajando en instalación"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>

              <div className="absolute bottom-6 left-6 right-6 text-white">
                <h4 className="text-xl font-bold mb-2">
                  Valores en Cada Proyecto
                </h4>
                <p className="text-white/90">
                  "Nuestro equipo vive estos valores en cada instalación,
                  creando no solo sistemas solares, sino relaciones duraderas."
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-secondary to-primary rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Experimenta Nuestros Valores
            </h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Te invitamos a conocer de primera mano cómo nuestros valores se
              traducen en una experiencia excepcional y resultados duraderos.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/calculadora">
                <button className="bg-white text-primary px-6 py-3 rounded-lg font-semibold solar-transition hover:bg-white/90">
                  Solicitar Cotización
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValuesSection;
